'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { Clock, WifiOff } from 'lucide-react';
import { getFixtureEvents, type FixtureEvent } from '@/lib/api-football';
import { subscribeToEventUpdates, type EventUpdatePayload } from '@/lib/supabase-realtime';
import { useLanguage } from '@/contexts/LanguageContext';

const FALLBACK_EVENTS: FixtureEvent[] = [
  { time: { elapsed: 1, extra: null }, team: { id: 6, name: 'Brazil', logo: '' }, player: { id: null, name: 'Kick Off' }, assist: { id: null, name: null }, type: 'kickoff', detail: 'Brazil kick off', comments: null },
  { time: { elapsed: 23, extra: null }, team: { id: 6, name: 'Brazil', logo: '' }, player: { id: null, name: 'Vinicius Jr.' }, assist: { id: null, name: 'Rodrygo' }, type: 'Goal', detail: 'Normal Goal', comments: null },
  { time: { elapsed: 41, extra: null }, team: { id: 6, name: 'Brazil', logo: '' }, player: { id: null, name: 'Raphinha' }, assist: { id: null, name: 'Bruno G.' }, type: 'Goal', detail: 'Header', comments: null },
  { time: { elapsed: 45, extra: null }, team: { id: 7, name: 'Argentina', logo: '' }, player: { id: null, name: 'Half Time' }, assist: { id: null, name: null }, type: 'halftime', detail: '2 min added time', comments: null },
  { time: { elapsed: 52, extra: null }, team: { id: 7, name: 'Argentina', logo: '' }, player: { id: null, name: 'Alvarez' }, assist: { id: null, name: 'Lautaro' }, type: 'subst', detail: 'Substitution', comments: null },
  { time: { elapsed: 55, extra: null }, team: { id: 7, name: 'Argentina', logo: '' }, player: { id: null, name: 'L. Martínez' }, assist: { id: null, name: 'De Paul' }, type: 'Goal', detail: 'Penalty', comments: null },
  { time: { elapsed: 62, extra: null }, team: { id: 7, name: 'Argentina', logo: '' }, player: { id: null, name: 'Paredes' }, assist: { id: null, name: null }, type: 'Card', detail: 'Yellow Card', comments: null },
  { time: { elapsed: 64, extra: null }, team: { id: 6, name: 'Brazil', logo: '' }, player: { id: null, name: 'VAR Review' }, assist: { id: null, name: null }, type: 'Var', detail: 'Handball check — no penalty', comments: null },
];

function EventIcon({ type, detail }: { type: string; detail: string }) {
  const t = type.toLowerCase();
  const d = detail.toLowerCase();
  if (t === 'goal') return <span className="text-base">⚽</span>;
  if (t === 'card' && d.includes('yellow')) return <span className="w-3.5 h-4 rounded-sm inline-block flex-shrink-0" style={{ background: 'hsl(45 93% 47%)' }} />;
  if (t === 'card' && d.includes('red')) return <span className="w-3.5 h-4 rounded-sm inline-block flex-shrink-0 bg-red-500" />;
  if (t === 'subst') return <span className="text-base">🔄</span>;
  if (t === 'var') return <span className="text-xs font-bold text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded">VAR</span>;
  return <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: 'hsl(222 25% 30%)' }} />;
}

export default function MatchTimeline() {
  const searchParams = useSearchParams();
  const fixtureId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const { t } = useLanguage();

  const [events, setEvents] = useState<FixtureEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!fixtureId) {
      setEvents(FALLBACK_EVENTS);
      setUsingFallback(true);
      setLoading(false);
      return;
    }
    try {
      const data = await getFixtureEvents(fixtureId);
      if (data && data.length > 0) {
        setEvents(data);
        setUsingFallback(false);
      } else {
        if (events.length === 0) {
          setEvents(FALLBACK_EVENTS);
          setUsingFallback(true);
        }
      }
    } catch {
      if (events.length === 0) {
        setEvents(FALLBACK_EVENTS);
        setUsingFallback(true);
      }
    } finally {
      setLoading(false);
    }
  }, [fixtureId, events.length]);

  useEffect(() => {
    fetchEvents();
    intervalRef.current = setInterval(fetchEvents, 15_000);

    // Subscribe to real-time event updates
    const targetId = fixtureId ?? 9001;
    const unsub = subscribeToEventUpdates(targetId, (payload: EventUpdatePayload) => {
      const newEvent: FixtureEvent = {
        time: { elapsed: payload.minute, extra: null },
        team: { id: 0, name: payload.team, logo: '' },
        player: { id: null, name: payload.player },
        assist: { id: null, name: null },
        type: payload.type,
        detail: payload.detail,
        comments: null,
      };
      setEvents(prev => [...prev, newEvent]);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (unsub) unsub();
    };
  }, [fetchEvents, fixtureId]);

  const isSpecial = (ev: FixtureEvent) => {
    const t = ev.type.toLowerCase();
    return t === 'kickoff' || t === 'halftime' || ev.player.name === 'Half Time' || ev.player.name === 'Kick Off';
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
    >
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
        <div className="flex items-center gap-2">
          <Clock size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.matchTimeline}</h3>
        </div>
        {usingFallback && (
          <span className="flex items-center gap-1 text-xs text-amber-400">
            <WifiOff size={10} />
            {t.gameDetail.cached}
          </span>
        )}
      </div>

      <div className="relative px-4 py-4 space-y-1">
        {/* Center line */}
        <div className="absolute left-1/2 top-4 bottom-4 w-px" style={{ background: 'hsl(222 25% 18%)' }} />

        {loading ? (
          [1, 2, 3, 4].map(i => (
            <div key={i} className="h-10 rounded-lg animate-pulse my-2" style={{ background: 'hsl(222 35% 14%)' }} />
          ))
        ) : (
          events.map((ev, idx) => {
            const special = isSpecial(ev);
            const isGoal = ev.type.toLowerCase() === 'goal';
            // Determine home/away by checking if it's the first team in events
            const firstTeam = events.find(e => !isSpecial(e))?.team.name;
            const isHome = ev.team.name === firstTeam;

            if (special) {
              return (
                <div key={`ev-${idx}`} className="flex items-center justify-center gap-2 py-2 relative z-10">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
                    style={{ background: 'hsl(222 35% 14%)', border: '1px solid hsl(222 25% 20%)', color: 'hsl(222 15% 55%)' }}
                  >
                    <span className="font-mono tabular-nums font-semibold">{ev.time.elapsed}&apos;</span>
                    <span>{ev.player.name}</span>
                    {ev.detail && <span className="text-slate-600">· {ev.detail}</span>}
                  </div>
                </div>
              );
            }

            return (
              <div key={`ev-${idx}`} className={`flex items-start gap-3 py-2 relative z-10 ${isHome ? 'flex-row' : 'flex-row-reverse'}`}>
                <div className={`flex-1 flex flex-col ${isHome ? 'items-start' : 'items-end'}`}>
                  <div
                    className="rounded-lg px-3 py-2 max-w-[90%]"
                    style={{
                      background: isGoal ? 'hsl(25 95% 53% / 0.12)' : 'hsl(222 35% 14%)',
                      border: `1px solid ${isGoal ? 'hsl(25 95% 53% / 0.25)' : 'hsl(222 25% 18%)'}`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <EventIcon type={ev.type} detail={ev.detail} />
                      <span className="text-sm font-semibold text-white">{ev.player.name}</span>
                    </div>
                    {ev.assist.name && (
                      <p className="text-xs text-slate-500 mt-0.5">{t.gameDetail.assist}: {ev.assist.name}</p>
                    )}
                    {ev.detail && (
                      <p className="text-xs text-slate-600 mt-0.5">{ev.detail}</p>
                    )}
                  </div>
                </div>

                <div
                  className="flex-shrink-0 w-10 h-7 rounded-full flex items-center justify-center text-xs font-bold font-mono tabular-nums relative z-10"
                  style={{
                    background: isGoal ? 'hsl(25 95% 53%)' : 'hsl(222 35% 16%)',
                    color: isGoal ? 'white' : 'hsl(222 15% 60%)',
                    border: '2px solid hsl(222 35% 11%)',
                  }}
                >
                  {ev.time.elapsed}&apos;
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}